import { useState } from "react";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import LabeledSelect from "~/components/common/forms/LabeledSelect";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import { Permission } from "~/hooks/permissions";
import type { LocalVisitResourceFriction } from "~/utils/driveTimeOptimizerConversion";

/**
 * A short menu open/close animation: quick enough to feel instant now that the
 * underlying re-render cost has been fixed, but still smooth.
 */
export const MENU_TRANSITION_DURATION_MS = 75;

export type OptimizeButtonStatus = "default" | "loading" | "check" | "error";

export type AdvancedOptimizeSettings = {
  arrivalWindowAdjustment: number | undefined;
  defaultStartTimeFriction: LocalVisitResourceFriction;
  defaultAssignmentFriction: LocalVisitResourceFriction;
  ignoreShouldKeepOptimizationResult: boolean;
};

type OptimizeButtonProps = {
  disabled: boolean;
  status?: OptimizeButtonStatus;
  onAssignMissingProviders: (ignoreShouldKeepOptimizationResult: boolean) => void;
  onOptimizeAdvanced: (settings: AdvancedOptimizeSettings) => void;
};

export const OPTIMIZE_EVERYTHING_HELP =
  "Optimize the entire schedule. Attempt to keep the same provider and time, but change either if it fixes conflicts, reduces drive times significantly, or reduces gaps (freeing up larger blocks of time)";

export const OPTIMIZE_EACH_PROVIDER_HELP =
  "Keep all visits on the same provider, but shift around times to fix conflicts, reduce drive times and remove gaps.";

export const ASSIGN_MISSING_PROVIDERS_HELP =
  "Assign all unassigned and out-of-shift visits. On multi-resource visits, attempt to keep the same providers, but change them if necessary to make the schedule possible.";

const ARRIVAL_WINDOW_OPTIONS: { label: string; minutes: number }[] = [
  { label: "0 minutes", minutes: 0 },
  { label: "15 minutes", minutes: 15 },
  { label: "30 minutes", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "2 hours", minutes: 120 },
  { label: "4 hours", minutes: 240 },
];

const FRICTION_OPTIONS: { label: string; value: LocalVisitResourceFriction }[] = [
  { label: "None", value: "none" },
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
  { label: "Frozen", value: "frozen" },
];

const USE_EXISTING_ARRIVAL_WINDOW = "existing";

const parseFriction = (value: string): LocalVisitResourceFriction => {
  switch (value) {
    case "low":
    case "medium":
    case "high":
    case "frozen":
      return value;
    default:
      return "none";
  }
};

const statusIcon = (status: OptimizeButtonStatus) => {
  switch (status) {
    case "loading":
      return <CircularProgress size={16} color="inherit" />;
    case "check":
      return <CheckIcon color="success" />;
    case "error":
      return <CloseIcon color="error" />;
    case "default":
      return <AutoFixHighIcon />;
  }
};

/**
 * The Optimize button and its options menu.
 *
 * This intentionally owns the menu's open/anchor state locally rather than in
 * the parent command view. The command view also renders the heavy calendar,
 * so keeping the menu state here means opening the menu only re-renders this
 * small component instead of the entire command center, making it pop
 * instantly (matching the Add menu's behavior).
 */
const OptimizeButton = ({
  disabled,
  status = "default",
  onAssignMissingProviders,
  onOptimizeAdvanced,
}: OptimizeButtonProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [arrivalWindow, setArrivalWindow] = useState(USE_EXISTING_ARRIVAL_WINDOW);
  const [timeFriction, setTimeFriction] = useState<LocalVisitResourceFriction>("none");
  const [assignmentFriction, setAssignmentFriction] = useState<LocalVisitResourceFriction>("none");
  const [ignoreKeepResults, setIgnoreKeepResults] = useState(true);
  const [ignoreShouldKeepOptimizationResult, setIgnoreShouldKeepOptimizationResult] = useState(false);
  const menuOpen = Boolean(menuAnchorEl);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleOptimizeEverything = () => {
    handleMenuClose();
    onOptimizeAdvanced({
      arrivalWindowAdjustment: undefined,
      defaultStartTimeFriction: "low",
      defaultAssignmentFriction: "low",
      ignoreShouldKeepOptimizationResult,
    });
  };

  const handleOptimizeEachProvider = () => {
    handleMenuClose();
    onOptimizeAdvanced({
      arrivalWindowAdjustment: undefined,
      defaultStartTimeFriction: "medium",
      defaultAssignmentFriction: "frozen",
      ignoreShouldKeepOptimizationResult,
    });
  };

  const handleAdvancedOpen = () => {
    setArrivalWindow(USE_EXISTING_ARRIVAL_WINDOW);
    setTimeFriction("none");
    setAssignmentFriction("none");
    setIgnoreKeepResults(true);
    handleMenuClose();
    setAdvancedOpen(true);
  };

  const handleAdvancedClose = () => setAdvancedOpen(false);

  const handleAdvancedRun = () => {
    setAdvancedOpen(false);
    onOptimizeAdvanced({
      arrivalWindowAdjustment: arrivalWindow === USE_EXISTING_ARRIVAL_WINDOW
        ? undefined
        : Number(arrivalWindow),
      defaultStartTimeFriction: timeFriction,
      defaultAssignmentFriction: assignmentFriction,
      ignoreShouldKeepOptimizationResult: ignoreKeepResults,
    });
  };

  return (
    <>
      <Button
        onClick={handleButtonClick}
        disabled={disabled}
        variant="outlined"
        endIcon={statusIcon(status)}
      >
        Optimize
      </Button>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transitionDuration={MENU_TRANSITION_DURATION_MS}
      >
        <OptimizeFlowRow
          label="Entire Schedule"
          help={OPTIMIZE_EVERYTHING_HELP}
          disabled={disabled}
          onClick={handleOptimizeEverything}
        />
        <OptimizeFlowRow
          label="Each Provider"
          help={OPTIMIZE_EACH_PROVIDER_HELP}
          disabled={disabled}
          onClick={handleOptimizeEachProvider}
        />
        <OptimizeFlowRow
          label="Visits Missing Providers"
          help={ASSIGN_MISSING_PROVIDERS_HELP}
          disabled={disabled}
          onClick={() => {
            handleMenuClose();
            onAssignMissingProviders(ignoreShouldKeepOptimizationResult);
          }}
        />
        <PermissionOrHide permission={Permission.debugApps}>
          <MenuItem
            onClick={handleAdvancedOpen}
            disabled={disabled}
          >
            Advanced…
          </MenuItem>
        </PermissionOrHide>
        <PermissionOrHide permission={Permission.debugApps}>
          <Box
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIgnoreShouldKeepOptimizationResult(!ignoreShouldKeepOptimizationResult);
            }}
            sx={{
              px: 2,
              py: 1,
              mt: 0.5,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              cursor: "pointer",
            }}
          >
            <Typography variant="inherit">
              Ignore keep-results check
            </Typography>
            <Switch
              size="small"
              checked={ignoreShouldKeepOptimizationResult}
              onClick={(event) => {
                event.stopPropagation();
              }}
              onChange={(event) => {
                setIgnoreShouldKeepOptimizationResult(event.target.checked);
                event.stopPropagation();
              }}
            />
          </Box>
        </PermissionOrHide>
      </Menu>

      <Dialog open={advancedOpen} onClose={handleAdvancedClose} fullWidth maxWidth="xs">
        <DialogTitle>Advanced</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <LabeledSelect
              label="Arrival window"
              value={arrivalWindow}
              fullWidth
              size="small"
              onChange={(event) => setArrivalWindow(event.target.value)}
            >
              <MenuItem value={USE_EXISTING_ARRIVAL_WINDOW}>Use existing</MenuItem>
              {ARRIVAL_WINDOW_OPTIONS.map(({ label, minutes }) => (
                <MenuItem key={minutes} value={String(minutes)}>
                  {label}
                </MenuItem>
              ))}
            </LabeledSelect>
            <LabeledSelect
              label="Time friction"
              value={timeFriction}
              fullWidth
              size="small"
              onChange={(event) => setTimeFriction(parseFriction(event.target.value))}
            >
              {FRICTION_OPTIONS.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </LabeledSelect>
            <LabeledSelect
              label="Assignment friction"
              value={assignmentFriction}
              fullWidth
              size="small"
              onChange={(event) => setAssignmentFriction(parseFriction(event.target.value))}
            >
              {FRICTION_OPTIONS.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </LabeledSelect>
            <FormControlLabel
              control={(
                <Switch
                  checked={ignoreKeepResults}
                  onChange={(event) => setIgnoreKeepResults(event.target.checked)}
                />
              )}
              label="Ignore keep-results check"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAdvancedClose}>Cancel</Button>
          <Button onClick={handleAdvancedRun} variant="contained" disabled={disabled}>
            Run
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const OptimizeFlowRow = ({
  label,
  help,
  disabled,
  onClick,
}: {
  label: string;
  help: string;
  disabled: boolean;
  onClick: () => void;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", pr: 0.5 }}>
    <MenuItem onClick={onClick} disabled={disabled} sx={{ flexGrow: 1 }}>
      {label}
    </MenuItem>
    <Tooltip title={help}>
      <IconButton
        size="small"
        aria-label={`About ${label}`}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <InfoOutlinedIcon fontSize="small" color="action" />
      </IconButton>
    </Tooltip>
  </Box>
);

export default OptimizeButton;
